import { apiClient } from '../../lib/api/client'
import type { components } from '../../lib/api/schema.gen'
import { toAddCartLineRequest } from '../cart/cart-catalog-lookup'
import type { RfqReviewLine } from './review-domain'

type Sourceable = components['schemas']['Sourceable']

/**
 * UID-263 (ADR-0087 D3/D4) — the single-line `POST /cart/lines` helper for
 * the per-row `CatalogAddToCartButton` (`PartsReviewTable.tsx`). UID-616:
 * the summary rail's bulk "Add N priced to cart" flow (`RfqReviewPage.tsx`)
 * now goes through `addLinesToCart`'s own `POST /cart/lines/add-batch` call
 * below instead of N calls to this function — one request cannot contend
 * with itself, closing the CAS-409 conflation a real concurrent fan-out of
 * this helper could hit. `cart/concurrent_modification` is rarer on THIS
 * single-line path than under the old fan-out, but still reachable from any
 * other concurrent writer on the same tenant's cart (another tab, another
 * user, a bulk batch in flight) — hence `classify409` below, not a bare
 * "409 is always success" shortcut.
 *
 * Classifies the outcome rather than throwing (ADR-0064's per-row
 * retry-safety model, applied here): a `409` ("already in cart," the
 * contract's one-line-per-Sourceable rule) is treated as a SUCCESS, not an
 * error — a retry that lands on an already-added line is the same outcome
 * from the customer's point of view. This REFINES the pre-UID-263 private
 * `addLineToCart` (`PartsReviewTable.tsx:518`), which threw on any error
 * including 409 — a small, deliberate behavior change to the shipped
 * per-row 409 handling, called out here and covered by tests in both
 * `cart-add.test.ts` and `PartsReviewTable.test.tsx`.
 *
 * Sends NO client price (ADR-0004) — same no-`acceptedQuote` posture as
 * `toAddCartLineRequest`'s plain catalog/custom add path; the BFF prices the
 * line itself.
 */
/** `already_in_cart` is only reachable from the single-line `addLineToCart`
 * path — under UID-615's upsert-replace semantics, `addLinesToCart`'s batch
 * reports an existing line that got replaced as `'added'` too (the batch
 * endpoint doesn't distinguish "inserted" from "replaced" in its response).
 * Don't remove this arm as "dead" without checking the per-row path first. */
export type CartAddOutcome = 'added' | 'already_in_cart' | 'failed'

export interface CartAddResult {
  readonly lineId: string
  readonly outcome: CartAddOutcome
  /** Only set when `outcome === 'failed'` — a human-readable reason, for a
   * toast or per-line diagnostic. */
  readonly message?: string
}

const GENERIC_FAILURE_MESSAGE = "We couldn't add this part to your cart. Try again."

/** `true` for either success classification (`added` or `already_in_cart`) —
 * the single place both call sites ask "did this line make it to the cart?" */
export function isCartAddSuccess(outcome: CartAddOutcome): boolean {
  return outcome === 'added' || outcome === 'already_in_cart'
}

function sourceableForCartOf(line: RfqReviewLine): Sourceable {
  return {
    id: line.sourceable.id,
    kind: line.sourceable.kind,
    uom: line.sourceable.uom,
    ...(line.sourceable.accountId !== undefined ? { accountId: line.sourceable.accountId } : {}),
    ...(line.name ? { name: line.name } : {}),
    ...(line.sku ? { catalogSku: line.sku } : {}),
  }
}

/** UID-616 — key on the `Problem.type` RFC 9457 discriminator, not the bare
 * HTTP status: `cart/duplicate_line` (already there) is success, but
 * `cart/concurrent_modification` (CAS exhausted, the line was NOT written)
 * is a real failure that a bare `status === 409` check cannot tell apart
 * from the success case. Falls back to the old status-only check when no
 * `type` is present (Gate 5 S2 — an empty/absent Problem body on a 409 must
 * still classify as already-present/success). Deliberately NOT used by
 * `addLinesToCart` below — the batch endpoint never returns 409 at all (an
 * already-present Sourceable is upserted, not rejected), so every batch
 * error is genuinely `'failed'` regardless of `type`; wiring this in there
 * would silently upgrade a real batch failure to success if the contract
 * ever changed to emit a `duplicate_line`-shaped error. */
function classify409(problemType: string | undefined): 'already_in_cart' | 'failed' {
  return problemType === 'cart/concurrent_modification' ? 'failed' : 'already_in_cart'
}

export async function addLineToCart(line: RfqReviewLine): Promise<CartAddResult> {
  try {
    const { data, error, response } = await apiClient.POST('/cart/lines', {
      body: toAddCartLineRequest(sourceableForCartOf(line), {
        value: line.quantity,
        uom: line.sourceable.uom.saleUom,
      }),
    })
    if (data) return { lineId: line.id, outcome: 'added' }

    // Gate 5 S2 — read the REAL HTTP status (`response.status`) as well as
    // the `Problem` body's own `status` field: a 409 with an empty/absent
    // Problem body (no JSON body, or a body that doesn't echo `status`)
    // must still classify as already-present/success. `error?.status` is
    // checked first (cheap, no extra property access) but `response.status`
    // is the authoritative source of truth — it's set on every response,
    // Problem body or not.
    const status = (error as { status?: number } | undefined)?.status ?? response?.status
    const detail = (error as { detail?: string } | undefined)?.detail
    if (status === 409) {
      const outcome = classify409((error as { type?: string } | undefined)?.type)
      return outcome === 'already_in_cart'
        ? { lineId: line.id, outcome }
        : { lineId: line.id, outcome, message: detail ?? GENERIC_FAILURE_MESSAGE }
    }

    return { lineId: line.id, outcome: 'failed', message: detail ?? GENERIC_FAILURE_MESSAGE }
  } catch {
    // A thrown network/parse error (rather than a typed `error` body) — same
    // generic, honest failure classification; never silently swallowed as a
    // false "added".
    return { lineId: line.id, outcome: 'failed', message: GENERIC_FAILURE_MESSAGE }
  }
}

/**
 * UID-616 — one `POST /cart/lines/add-batch` instead of N concurrent
 * `POST /cart/lines`. The old N-adds-in-parallel loop fired every line's
 * `addLineToCart` concurrently, and `CartService.mutate`'s optimistic CAS
 * gives up after 5 attempts under that much self-contention, surfacing
 * `cart/concurrent_modification` 409s that the old per-line classifier
 * conflated with `cart/duplicate_line` (both "just" 409) — silently losing
 * lines while reporting them added. One request cannot contend with
 * itself, so that conflation becomes unreachable here. The batch is
 * all-or-nothing (UID-403): on success, every requested Sourceable is
 * necessarily present in the returned cart; on failure, every line is
 * `'failed'` with the server's detail. Keeps this function's existing
 * contract — one `CartAddResult` per input line — so `summarizeCartAddBatch`
 * and `RfqReviewPage`'s per-row rendering need no change.
 *
 * Relies on the caller never sending the SAME `sourceable.id` twice in one
 * batch — `RfqReviewPage`'s `AddPartsModal` already excludes ids already in
 * the review lines, so a duplicate can't reach here today. If that ever
 * regressed, the batch endpoint's own contract resolves a repeated id to its
 * LAST occurrence, and both input lines would read as `'added'` here —
 * silently discarding the first occurrence's quantity.
 */
export async function addLinesToCart(
  lines: readonly RfqReviewLine[],
): Promise<readonly CartAddResult[]> {
  if (lines.length === 0) return []
  try {
    const { data, error } = await apiClient.POST('/cart/lines/add-batch', {
      body: {
        lines: lines.map((line) =>
          toAddCartLineRequest(sourceableForCartOf(line), {
            value: line.quantity,
            uom: line.sourceable.uom.saleUom,
          }),
        ),
      },
    })
    if (data) {
      const cartSourceableIds = new Set(data.lines.map((l) => l.sourceable.id))
      return lines.map((line) => {
        const added = cartSourceableIds.has(line.sourceable.id)
        return added
          ? { lineId: line.id, outcome: 'added' as const }
          : { lineId: line.id, outcome: 'failed' as const, message: GENERIC_FAILURE_MESSAGE }
      })
    }

    const detail = (error as { detail?: string } | undefined)?.detail
    return lines.map((line) => ({
      lineId: line.id,
      outcome: 'failed',
      message: detail ?? GENERIC_FAILURE_MESSAGE,
    }))
  } catch {
    return lines.map((line) => ({
      lineId: line.id,
      outcome: 'failed',
      message: GENERIC_FAILURE_MESSAGE,
    }))
  }
}

/**
 * UID-263 (ADR-0087 D3) — partial-failure-honest summary of a batch add,
 * pure so it's independently unit-testable from the POST plumbing. Never
 * reports a blanket success when any line failed (ADR-0064 properties #1/#2).
 */
export interface CartAddBatchSummary {
  readonly addedCount: number
  readonly failedCount: number
  readonly addedLineIds: readonly string[]
  readonly failedLineIds: readonly string[]
  readonly message: string
  readonly tone: 'success' | 'error'
}

export function summarizeCartAddBatch(results: readonly CartAddResult[]): CartAddBatchSummary {
  const added = results.filter((result) => isCartAddSuccess(result.outcome))
  const failed = results.filter((result) => result.outcome === 'failed')
  const addedCount = added.length
  const failedCount = failed.length
  const total = results.length

  let message: string
  let tone: 'success' | 'error'
  if (failedCount === 0) {
    message = `Added ${addedCount} part${addedCount === 1 ? '' : 's'} to cart.`
    tone = 'success'
  } else if (addedCount === 0) {
    message = `Couldn't add any parts to cart. Try again.`
    tone = 'error'
  } else {
    message = `Added ${addedCount} of ${total} to cart. ${failedCount} could not be added — try again.`
    tone = 'error'
  }

  return {
    addedCount,
    failedCount,
    addedLineIds: added.map((result) => result.lineId),
    failedLineIds: failed.map((result) => result.lineId),
    message,
    tone,
  }
}
